import { Browser } from "@capacitor/browser";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";

import { Platform } from "./platform";

function isInternalUrl(url: string): boolean {
  if (url.startsWith("//")) {
    return false;
  }
  if (url.startsWith("/") || url.startsWith("#")) {
    return true;
  }
  try {
    return new URL(url).origin === globalThis.location.origin;
  } catch {
    return false;
  }
}

/**
 * Returns a function that opens a URL in the appropriate context based on platform and URL type.
 *
 * On native mobile:
 * - Internal URLs (same origin) navigate via React Router to preserve the auth session and SPA state
 * - External URLs open in an in-app browser (SFSafariViewController / Chrome Custom Tabs)
 *
 * On web, opens a new tab.
 */
export function useOpenUrl() {
  const history = useHistory();

  return useCallback(
    async (url: string): Promise<void> => {
      if (Platform.isMobile) {
        if (isInternalUrl(url)) {
          const { pathname, search, hash } = new URL(url, globalThis.location.origin);
          history.push(`${pathname}${search}${hash}`);
        } else {
          await Browser.open({ url });
        }
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    },
    [history]
  );
}
